// Angular Files
import { Injectable } from '@angular/core';

// Angular Material Files
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

// Teller Online Library Files
// Deep-path imports avoid the components barrel, which has dialog.component re-importing
// from '../../services' — under Vite ESM that cycle leaves TellerOnlineMessageService
// missing from the shared barrel namespace, crashing R3Injector.get on hasOwnProperty.
import type { InfoMessage } from 'teller-online-libraries/help';
// This service, dialog.component, and validation.service form an intentional 3-way cycle
// (dialog.component injects validation.service; validation.service deep-path imports this
// service back). See TellerOnline.Frontend/CLAUDE.md "Vite barrel cycles".
// eslint-disable-next-line import/no-cycle
import { TellerOnlineDialogComponent } from '../components/dialog/dialog.component';
import { TellerOnlineDialogAction, TellerOnlineDialogOptions } from '../components/dialog/dialog.types';
import {
    TellerOnlineNotificationComponent,
    TellerOnlineNotificationData
} from '../components/notification/notification.component';

@Injectable()
export class TellerOnlineMessageService {
    constructor(
        private snackbar: MatSnackBar,
        private dialog: MatDialog
    ) {}

    /**
     * Display a temporary notification message near the top of the screen.
     * Will automatically disappear after a set amount of time.
     * A dismiss button is shown when the display time exceeds 3 seconds (or is unset),
     * allowing the user to manually dismiss the notification sooner.
     * @param message - the message to be displayed
     * @param type - what type of message this is - controls color and icon that is displayed, options are 'error', 'success', or 'info'
     * @param displayTime - the amount of time before the notification is auto-dismissed
     * @param position - the position relative to the screen (top, center, bottom, left, center, right)
     */
    public notification(
        message: string,
        type?: 'error' | 'success' | 'info' | 'caution',
        displayTime?: number,
        position: any = { horizontal: 'center', vertical: 'top' }
    ) {
        const panelClasses = ['notification'];

        if (type === 'error') {
            panelClasses.push('notification--error');
        } else if (type === 'caution') {
            panelClasses.push('notification--caution');
        }

        const dismissLabel = (displayTime ?? Infinity) > 3000 ? 'Dismiss' : '';

        const options: MatSnackBarConfig<TellerOnlineNotificationData> = {
            horizontalPosition: position.horizontal,
            verticalPosition: position.vertical,
            duration: displayTime,
            panelClass: panelClasses,
            data: { message, dismissLabel },
        };

        this.snackbar.openFromComponent(TellerOnlineNotificationComponent, options);
    }

    /**
     * A popup dialog that needs to be addressed before it will go away.
     * Similar to the browser built in "confirm" dialog,
     * prompts the user for "yes" or "no", closing in any other way is considered no.
     * @param html - the body of the dialog (exclusive of buttons), can be simple text or complex html
     * @param title - the title of the dialog
     * @returns a Promise that resolves to true if the "yes" button was clicked, false otherwise
     */
    public async prompt(
        html: string,
        title: string,
        noActionText: string = 'No',
        yesActionText: string = 'Yes'
    ): Promise<boolean> {
        const result = await this.custom({
            content: html,
            title,
            actions: [
                new TellerOnlineDialogAction({
                    text: noActionText,
                    close: false
                }),
                new TellerOnlineDialogAction({
                    text: yesActionText,
                    close: true
                }),
            ]
        });

        // A backdrop click or Escape closes the dialog with no value. Only the "yes" button
        // counts as yes: guards hand this result to the router, which goes on anything but false
        return result === true;
    }

    /**
     * A popup dialog that needs to be acknowledged before it will go away.
     * Similar to the browser built in "alert" dialog,
     * clicking okay, or closing it any other way, is treated the same
     * @param html - the body of the dialog (exclusive of buttons), can be simple text or complex html
     * @param title - the title of the dialog
     * @param dialogClass - An optional class to apply to the alert dialog for custom styling
     * @returns A promise that resolves to false once the dialog has been closed
     */
    public async alert(html: string, title: string, dialogClass?: string) {
        return this.custom({
            content: html,
            title,
            actions: [
                new TellerOnlineDialogAction({
                    text: 'OK',
                    close: false
                })
            ]
        }, dialogClass);
    }

    /**
     * A convenience method for showing an informational dialog.
     * Uses the 'info-dialog' panel class for constrained width styling.
     * @param messageOrInfoMessage - either the message string to be displayed, or an InfoMessage object
     * @param title - the title of the dialog (optional if InfoMessage is passed)
     * @returns A promise that resolves to false once the dialog has been closed
     */
    public async info(messageOrInfoMessage: string | InfoMessage, title?: string) {
        if (typeof messageOrInfoMessage === 'string') {
            return this.alert(messageOrInfoMessage, title, 'info-dialog');
        }
            return this.alert(messageOrInfoMessage.message, messageOrInfoMessage.title, 'info-dialog');
    }

    /**
     * A custom dialog that can contain any number of buttons (including 0), a title, and a body.
     * @param options - the options used to generate the dialog, including title, body (content), and buttons (actions)
     * @param dialogClass - An optional class to apply to the dialog for styling
     * @returns A promise that resolves when the dialog has been closed. Value is dependent on the actions (false, unless an action specifies close: true)
     */
    public custom(options: TellerOnlineDialogOptions, dialogClass?: string): Promise<any> {
        const dialog = this.dialog.open(TellerOnlineDialogComponent, { data: options, panelClass: dialogClass, ariaLabelledBy: 'aria_dialog_title' });

        return dialog.afterClosed().toPromise();
    }
}

// Dialog model types live in components/dialog/dialog.types.ts.
// Re-exported here for backwards-compat (external consumers import them from
// teller-online-libraries/shared, which surfaces this file via the barrel).
export {
    TellerOnlineDialogOptions,
    TellerOnlineDialogAction,
    TellerOnlineDialogTable,
    TellerOnlineColumnMetadata,
    ColumnContentAlignment,
} from '../components/dialog/dialog.types';
